import { Box, Icon, Theme, Typography, styled } from "@outschool/backpack";
import {
  Activity,
  Enrollment,
  Section,
} from "@outschool/gql-backend-generated";
import { faSearch } from "@outschool/icons";
import { useLocale, useTranslation } from "@outschool/localization";
import { enrolledPath, searchPath } from "@outschool/routes";
import {
  formatDateTimeWithWeekday,
  fullDurationStringWithStartTime,
  fullDurationStringWithoutTimes,
} from "@outschool/time";
import { TrackedButton } from "@outschool/ui-components-shared";
import { Loading } from "@outschool/ui-legacy-component-library";
import { useIsMobile, useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import { isFlexSchedule } from "../../../shared/Activity";
import { isFuture } from "../../../shared/Section";
import { useActiveLearnerSearchFilters } from "../../hooks/useActiveLearnerSearchFilters";
import { useActiveLearner } from "../../providers/ActiveLearnerProvider";
import { useSearchByLearner } from "../../providers/SearchByLearnerProvider";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import Link from "../Link";
import ClassItemActions from "./ClassItemActions";
import ClassItemInfo from "./ClassItemInfo";

export type EnrollmentWithSection = Enrollment & {
  section: Section;
};
interface Props {
  enrollments: EnrollmentWithSection[];
  error?: Error | null;
  emptyStateComponent?: React.ReactNode;
  loading?: boolean;
  completed?: boolean;
}

const ClassList = ({
  enrollments,
  error,
  loading = false,
  completed = false,
  emptyStateComponent,
}: Props) => {
  return (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: 24,
        [theme.breakpoints.down("md")]: {
          gap: 20,
        },
      })}
    >
      {loading && <Loading />}
      {!loading &&
        enrollments.map(enrollment => (
          <ClassItem
            key={enrollment.uid}
            enrollment={enrollment}
            completed={completed}
          />
        ))}
      {!loading && enrollments.length === 0 && emptyStateComponent}
      {!loading && error && (
        <Box
          flex
          sx={{
            justifyContent: "center",
            padding: "4.4rem 2rem",
            borderColor: "grey.100",
            borderStyle: "solid",
            borderWidth: "1px",
            borderRadius: "8px",
          }}
        >
          <Typography
            variant="subtitle1"
            color="grey.600"
            sx={{ textAlign: "center" }}
          >
            {error.message}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const removeYearFromTimeString = (timeString: string): string => {
  // Remove year patterns like ", 2025" or ", 2026" from the time string
  let result = timeString.replace(/, \d{4}/g, "");

  // Add comma before time (e.g., "7:00 AM", "9 AM", or "2:30 PM") at the end of the string
  result = result.replace(/(\s)(\d{1,2}(:\d{2})?\s*(AM|PM|am|pm))/g, ", $2");

  return result;
};

const useClassTime = (
  section: Section,
  activity: Activity,
  userTimeZone: string,
  completed?: boolean,
) => {
  const { t } = useTranslation("client\\components\\ClassList");

  if (completed) {
    return "";
  }
  if (activity.isClub) {
    return t("Unlimited access");
  }
  if (!!activity.is_ongoing_weekly) {
    if (section.meetings.length === 0) {
      return t("Not yet scheduled");
    }
    return formatDateTimeWithWeekday(
      section.meetings[0].start_time,
      userTimeZone,
    );
  }
  if (activity.hasTeacherSchedule) {
    if (isFuture(section)) {
      return formatDateTimeWithWeekday(section.start_time, userTimeZone);
    }
    return formatDateTimeWithWeekday(
      section.nextOngoingMeeting?.start_time || section.start_time,
      userTimeZone,
    );
  }
  if (isFlexSchedule(activity)) {
    return fullDurationStringWithoutTimes(
      section.start_time,
      section.end_time,
      userTimeZone,
    );
  }
  return removeYearFromTimeString(
    fullDurationStringWithStartTime(
      section.start_time,
      section.end_time,
      userTimeZone,
      false,
    ),
  );
};

interface ClassItemProps {
  enrollment: EnrollmentWithSection;
  completed?: boolean;
}

const ClassItem = ({ enrollment, completed }: ClassItemProps) => {
  const locale = useLocale();
  const { timeZone } = useTimeZone();
  const isMobile = useIsMobile();

  const { section, learner } = enrollment;
  const { activity, leader: sectionLeader } = section;
  const { details, leader: activityLeader } = activity;
  const { photo } = details;

  const classTime = useClassTime(section, activity, timeZone, completed);

  const leader = sectionLeader || activityLeader;
  if (!learner) {
    return null;
  }

  const ClassImage = styled("img")({ objectFit: "cover" });

  return (
    <Box
      flex
      sx={[
        (theme: Theme) => ({
          gap: 25,
          overflow: "hidden",
          alignItems: "center",
          borderColor: "grey.100",
          borderStyle: "solid",
          borderWidth: "1px",
          borderRadius: "8px",
          textDecoration: "none",
          color: "unset",
          "&:hover:not(:disabled), &:active:not(:disabled), &:focus:not(:disabled)":
            {
              textDecoration: "none",
              color: "unset",
            },
          [theme.breakpoints.down("md")]: {
            gap: 0,
            flexDirection: "column",
          },
        }),
      ]}
      component={Link}
      to={enrolledPath(section.uid, locale)}
    >
      <Box
        flex
        sx={(theme: Theme) => ({
          flex: 1,
          flexDirection: "row",
          alignItems: "center",
          [theme.breakpoints.down("md")]: {
            gap: 0,
          },
        })}
      >
        <ClassImage
          src={photo ? photo : ""}
          sx={(theme: Theme) => ({
            display: "flex",
            width: 110,
            minHeight: 110,
            height: "auto",
            alignSelf: "stretch",
            [theme.breakpoints.down("md")]: {
              width: 104,
            },
          })}
        />
        <Box
          flex
          sx={(theme: Theme) => ({
            flex: 1,
            flexDirection: "column",
            padding: "1.6rem 2.4rem",
            gap: 14,
            [theme.breakpoints.down("md")]: {
              gap: 8,
              padding: "1.2rem 1.4rem 1.2rem",
            },
          })}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            <Typography
              variant="h5"
              sx={(theme: Theme) => ({
                [theme.breakpoints.down("md")]: {
                  fontSize: "1.8rem",
                },
              })}
            >
              {activity.title}
            </Typography>
            <Typography
              variant="subtitle2"
              sx={(theme: Theme) => ({
                textAlign: "right",
                whiteSpace: "nowrap",
                [theme.breakpoints.down("md")]: {
                  display: "none",
                },
              })}
            >
              {classTime}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            <ClassItemInfo
              learnerName={learner.name ?? ""}
              leaderName={leader?.name ?? ""}
              section={section}
              activity={activity}
              classTime={classTime}
              enrollment={enrollment}
              completed={completed}
            />
            {!isMobile && (
              <ClassItemActions enrollment={enrollment} completed={completed} />
            )}
          </Box>
        </Box>
      </Box>
      {isMobile && (
        <Box
          flex
          sx={{
            width: "100%",
            borderTop: "1px solid",
            borderColor: "grey.100",
            padding: "1rem 0",
          }}
        >
          <ClassItemActions enrollment={enrollment} completed={completed} />
        </Box>
      )}
    </Box>
  );
};

export function EmptyListView({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  const Link = useLinkComponent();
  const { t } = useTranslation("client\\components\\ClassList");

  const { activeLearner } = useActiveLearner();
  const activeLearnerSearchFilters = useActiveLearnerSearchFilters();
  const { setLearner: setSearchByLearner } = useSearchByLearner();

  return (
    <Box
      flex
      sx={(theme: Theme) => ({
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        borderRadius: 10,
        padding: "0 1.6rem",
        height: 250,
        border: `1px solid ${theme.palette.neutral[100]}`,
        backgroundColor: theme.palette.neutral[50],
      })}
    >
      <Typography
        variant="body2"
        sx={(theme: Theme) => ({
          fontSize: "1.8rem",
          fontWeight: "medium",
          color: theme.palette.neutral[900],
          textAlign: "center",
        })}
      >
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          sx={(theme: Theme) => ({
            fontSize: "1.8rem",
            fontWeight: "medium",
            color: theme.palette.neutral[900],
            textAlign: "center",
          })}
        >
          {description}
        </Typography>
      )}

      <Link to={searchPath(activeLearnerSearchFilters(activeLearner))}>
        <TrackedButton
          trackingLabel="parent_dashboard_find_next_class"
          variant="outlined"
          sx={{ display: "flex", gap: "1rem", marginTop: "1.6rem" }}
          onClick={() => {
            setSearchByLearner(activeLearner);
          }}
        >
          <Icon icon={faSearch} /> {t("Browse Classes")}
        </TrackedButton>
      </Link>
    </Box>
  );
}

export default ClassList;
